import { computeActiveStatus } from '../../../billing-tool/utils/contract-utils';
import { SubscriptionEstimateType } from '../../types';
import { SubscriptionEstimateRow } from './types';

export const NULL_PLACEHOLDER = '--';

/**
 * Reserve is entered in the form as a positive amount but persisted negative:
 * it is contra-revenue (held back from the gross), so it always reduces the net.
 * These two helpers name that sign convention at the form/storage boundary.
 */
export const toStoredReserve = (formValue: number): number =>
  -Math.abs(formValue);

export const toFormReserve = (storedValue: number): number =>
  Math.abs(storedValue);

/**
 * Parses a `first.last@domain` email into "F. Last" (first initial + capitalized
 * last name). Falls back to the raw string on any unexpected format, or the
 * NULL placeholder when empty. Never throws on malformed input.
 */
export const formatLastModifiedBy = (lastModifiedBy: string): string => {
  if (!lastModifiedBy) {
    return NULL_PLACEHOLDER;
  }

  const localPart = lastModifiedBy.split('@')[0];
  const segments = localPart.split('.');

  if (segments.length < 2 || !segments[0] || !segments[1]) {
    return lastModifiedBy;
  }

  const firstInitial = segments[0].charAt(0).toUpperCase();
  const lastName = segments[1].charAt(0).toUpperCase() + segments[1].slice(1);

  return `${firstInitial}. ${lastName}`;
};

/**
 * Maps a SubscriptionEstimateType to a grid row view-model, resolving derived
 * display fields (net amount, active status, last-modified name) up front.
 */
export const toSubscriptionEstimateRow = (
  estimate: SubscriptionEstimateType,
): SubscriptionEstimateRow => ({
  id: estimate.id,
  grossAmount: estimate.grossAmount,
  reserveAmount: estimate.reserveAmount,
  netAmount: estimate.grossAmount + estimate.reserveAmount,
  startDate: estimate.startDate,
  endDate: estimate.endDate ?? null,
  status: computeActiveStatus(
    false,
    estimate.startDate,
    estimate.endDate ?? null,
  ),
  lastModifiedDisplay: formatLastModifiedBy(estimate.lastModifiedBy),
  updatedAt: estimate.updatedAt,
});
